/**
 * @fileoverview Entity Picker data model
 */
goog.declareModuleId('yext.ui.components.entitypicker.datasource.DataModel');

import {DataJson, EntityTypeJson, EntityJson, FolderJson, LabelJson, FolderEntitiesJson, LabelEntitiesJson} from '/ui/components/entitypicker/datasource/jsontypes';

/**
 * @typedef {{
 *   entities: {
 *     total: number,
 *     types: ?,
 *   },
 *   folders: {
 *     total: number,
 *     items: ?,
 *   },
 *   folderEntities: Map<number,?>,
 *   labels: {
 *     total: number,
 *     items: ?,
 *   },
 *   labelEntities: Map<number,?>,
 * }}
 */
let Data;

const EMPTY_DATA = {
  entities: {
    total: 0,
    types: [],
  },
  folders: {
    total: 0,
    items: [],
  },
  folderEntities: new Map(),
  labels: {
    total: 0,
    items: [],
  },
  labelEntities: new Map(),
};
Object.freeze(EMPTY_DATA);

/**
 * An immutable model of the entity picker's item data
 * NOTE(amullings): These objects are immutable, but this is done in a very
 * inefficient way. A better approach would be to use something like
 * immutability-helper.
 */
export class DataModel {
  /** @param {Data=} data */
  constructor(data) {
    const prevData = data || EMPTY_DATA;
    this.entities = {
      total: prevData.entities.total,
      types: prevData.entities.types.map(t => new EntityTypeModel(t)),
    };
    this.folders = {
      total: prevData.folders.total,
      items: prevData.folders.items.map(f => new FolderModel(f)),
    };
    this.folderEntities = new Map(
      Array.from(prevData.folderEntities.entries())
        .map(([folderId, folderEntities]) => [folderId, new FolderEntitiesModel(folderEntities)]),
    );
    this.labels = {
      total: prevData.labels.total,
      items: prevData.labels.items.map(l => new LabelModel(l)),
    };
    this.labelEntities = new Map(
      Array.from(prevData.labelEntities.entries())
        .map(([labelId, labelEntities]) => [labelId, new LabelEntitiesModel(labelEntities)]),
    );
    Object.freeze(this);
  }

  /**
   * @param {!Array<!EntityTypeModel>} types
   * @returns {!DataModel}
   */
  withEntityTypes(types) {
    const prevData = {
      entities: {
        total: this.entities.total,
        types: this.entities.types.map(type => {
          const newType = types.find(t => t.id == type.id);
          if (newType) {
            return newType;
          }
          return type;
        }),
      },
      folders: this.folders,
      folderEntities: this.folderEntities,
      labels: this.labels,
      labelEntities: this.labelEntities,
    };

    return new DataModel(prevData);
  }

  /**
   * @param {function(!EntityTypeModel): boolean} typeFilterFunc
   * @returns {!DataModel}
   */
  filterEntityTypes(typeFilterFunc) {
    const prevData = {
      ...this,
      entities: {
        ...this.entities,
        types: this.entities.types.filter(typeFilterFunc),
      },
    };

    return new DataModel(/** @type {Data} */(prevData));
  }

  /**
   * Returns a new data object containing the types, entities, folders, and
   * labels to the from both `this` and `other`, ignoring duplicates.
   * @param {!DataModel} other
   * @return {!DataModel}
   */
  mergeIn(other) {
    /** @type {!Array<EntityTypeModel>} */
    const newTypes = [];

    for (const type of this.entities.types) {
      const destType = other.entities.types.find(t => t.id == type.id);
      if (destType) {
        newTypes.push(type.mergeIn(destType));
      } else {
        newTypes.push(type);
      }
    }

    for (const type of other.entities.types) {
      if (!this.entities.types.find(t => t.id == type.id)) {
        newTypes.push(type);
      }
    }

    const result = new DataModel({
      entities: {
        total: other.entities.total,
        types: newTypes,
      },
      folders: {
        total: other.folders.total,
        items: mergeLists(this.folders.items, other.folders.items),
      },
      folderEntities: this.folderEntities,
      labels: {
        total: other.labels.total,
        items: mergeLists(this.labels.items, other.labels.items),
      },
      labelEntities: this.labelEntities,
    });
    other.folderEntities.forEach((newObj, folderId) => {
      const origObj = result.folderEntities.get(folderId);
      result.folderEntities.set(folderId, origObj ? origObj.mergeIn(newObj) : newObj);
    });
    other.labelEntities.forEach((newObj, labelId) => {
      const origObj = result.labelEntities.get(labelId);
      result.labelEntities.set(labelId, origObj ? origObj.mergeIn(newObj) : newObj);
    });

    return result;
  }

  /**
   * @param {!DataJson} data
   * @return {!DataModel}
   */
  static fromJson(data) {
    const entities = data['entities'];
    const folders = data['folders'];
    const folderEntities = data['folderEntities'] || {};
    const labels = data['labels'];
    const labelEntities = data['labelEntities'] || {};
    const newData = {
      entities: {
        total: entities ? entities['total'] : 0,
        types: entities ? entities['types'].map(EntityTypeModel.fromJson) : [],
      },
      folders: {
        total: folders ? folders['total'] : 0,
        items: folders ? folders['items'].map(FolderModel.fromJson) : [],
      },
      folderEntities: new Map(
        Object.entries(folderEntities)
          .map(([folderIdStr, folderEntitiesJson]) =>
            [+folderIdStr, FolderEntitiesModel.fromJson(folderEntitiesJson)],
          ),
      ),
      labels: {
        total: labels ? labels['total'] : 0,
        items: labels ? labels['items'].map(LabelModel.fromJson) : [],
      },
      labelEntities: new Map(
        Object.entries(labelEntities)
          .map(([labelIdStr, labelEntitiesJson]) =>
            [+labelIdStr, LabelEntitiesModel.fromJson(labelEntitiesJson)],
          ),
      ),
    };
    return new DataModel(newData);
  }
}

/**
 * An immutable model of the entity picker's entity type data
 */
export class EntityTypeModel {
  constructor(type) {
    /** @type {number} */
    this.total = type.total;
    /** @type {number} */
    this.id = type.id;
    /** @type {string} */
    this.label = type.label;
    /** @type {string|undefined} */
    this.apiName = type.apiName;
    /** @type {!Array<!EntityModel>} */
    this.entities = type.entities.map(e => new EntityModel(e));
    /** @type {string|undefined} */
    this.nextPageQuery = type.nextPageQuery;
    Object.freeze(this);
  }

  /**
   * @param {!EntityTypeJson} type
   * @return {!EntityTypeModel}
   */
  static fromJson(type) {
    return new EntityTypeModel({
      total: type['total'],
      id: type['id'],
      label: type['label'],
      apiName: type['apiName'],
      entities: (type['entities'] || []).map(EntityModel.fromJson),
      nextPageQuery: type['nextPageQuery'],
    });
  }

  /**
   * @param {!EntityTypeModel} other
   * @returns {!EntityTypeModel}
   */
  mergeIn(other) {
    const result = {
      total: other.total,
      id: other.id,
      label: other.label,
      entities: mergeLists(this.entities, other.entities),
      nextPageQuery: other.nextPageQuery,
    };

    return new EntityTypeModel(result);
  }
}

/**
 * An immutable model of the entity picker's entity data
 */
export class EntityModel {
  constructor(entity) {
    this.id = entity.id;
    this.externalId = entity.externalId;
    this.typeId = entity.typeId;
    this.label = entity.label;
    this.sublabel = entity.sublabel;
    Object.freeze(this);
  }

  /**
   * @param {!EntityJson} item
   * @return {!EntityModel}
   */
  static fromJson(item) {
    return new EntityModel({
      id: item['id'],
      externalId: item['externalId'],
      typeId: item['typeId'],
      label: item['label'],
      sublabel: item['sublabel'],
    });
  }
}

/**
 * An immutable model of the entity picker's folder data
 */
export class FolderModel {
  constructor(folder) {
    this.id = folder.id;
    this.label = folder.label;
    this.parentId = folder.parentId;
    Object.freeze(this);
  }

  /**
   * @param {!FolderJson} item
   * @return {!FolderModel}
   */
  static fromJson(item) {
    return new FolderModel({
      id: item['id'],
      label: item['label'],
      parentId: item['parentId'],
    });
  }
}

/**
 * An immutable model that represents the relationship between folders and
 * entities
 */
export class FolderEntitiesModel {
  /**
   * @param {{
   *   folderId: number,
   *   total: number,
   *   nextPageQuery: (string|undefined),
   *   entities: !Array<!EntityModel>,
   * }} orig
   */
  constructor(orig) {
    this.folderId = orig.folderId;
    this.total = orig.total;
    this.nextPageQuery = orig.nextPageQuery;
    this.entities = orig.entities.map(e => new EntityModel(e));
    Object.freeze(this);
  }

  /**
   * @param {!FolderEntitiesModel} other
   * @returns {!FolderEntitiesModel}
   */
  mergeIn(other) {
    return new DataModel.FolderEntitiesModel({
      folderId: other.folderId,
      total: other.total,
      nextPageQuery: other.nextPageQuery,
      entities: mergeLists(this.entities, other.entities),
    });
  }

  /**
   * @param {!FolderEntitiesJson} item
   * @return {!FolderEntitiesModel}
   */
  static fromJson(item) {
    return new FolderEntitiesModel({
      folderId: item['folderId'],
      total: item['total'],
      nextPageQuery: item['nextPageQuery'],
      entities: item['entities'].map(EntityModel.fromJson),
    });
  }
}

/**
 * An immutable model of the entity picker's label data
 */
export class LabelModel {
  constructor(label) {
    this.id = label.id;
    this.label = label.label;
    this.isInternal = label.isInternal;
    Object.freeze(this);
  }

  /**
   * @param {!LabelJson} item
   * @return {!LabelModel}
   */
  static fromJson(item) {
    return new LabelModel({
      id: item['id'],
      label: item['label'],
      isInternal: item['isInternal'],
    });
  }
}

/**
 * An immutable model that represents the relationship between labels and
 * entities
 */
export class LabelEntitiesModel {
  /**
   * @param {{
   *   labelId: number,
   *   total: number,
   *   nextPageQuery: (string|undefined),
   *   entities: !Array<!EntityModel>,
   * }} orig
   */
  constructor(orig) {
    this.labelId = orig.labelId;
    this.total = orig.total;
    this.nextPageQuery = orig.nextPageQuery;
    this.entities = orig.entities.map(e => new EntityModel(e));
    Object.freeze(this);
  }

  /**
   * @param {!LabelEntitiesModel} other
   * @returns {!LabelEntitiesModel}
   */
  mergeIn(other) {
    return new DataModel.LabelEntitiesModel({
      labelId: other.labelId,
      total: other.total,
      nextPageQuery: other.nextPageQuery,
      entities: mergeLists(this.entities, other.entities),
    });
  }

  /**
   * @param {!LabelEntitiesJson} item
   * @return {!LabelEntitiesModel}
   */
  static fromJson(item) {
    return new LabelEntitiesModel({
      labelId: item['labelId'],
      total: item['total'],
      nextPageQuery: item['nextPageQuery'],
      entities: item['entities'].map(EntityModel.fromJson),
    });
  }
}

/**
 * @template {{id: number}} T
 * @param {!Array<T>} list1
 * @param {!Array<T>} list2
 * @return {!Array<T>}
 */
function mergeLists(list1, list2) {
  const nextIds = new Set(list2.map(item => item.id));
  return list1.filter(item => !nextIds.has(item.id))
    .concat(list2);
}

/**
 * @param {EntityTypeModel} type
 * @param {string} text
 * @returns {boolean}
 */
export function entityTypeMatchesText(type, text) {
  if (type.apiName == null) {
    // Data is coming from an older version of UxComponentServer, which checks
    // the search term against the type label and apiName on the backend.
    return true;
  }
  const lowerCaseSearchTerm = text.toLowerCase();
  return !!text && (
    type.label.toLowerCase().includes(lowerCaseSearchTerm)
    || (!!type.apiName && type.apiName.toLowerCase().includes(lowerCaseSearchTerm))
  );
}

DataModel.EntityTypeModel = EntityTypeModel;
DataModel.EntityModel = EntityModel;
DataModel.FolderModel = FolderModel;
DataModel.FolderEntitiesModel = FolderEntitiesModel;
DataModel.LabelModel = LabelModel;
DataModel.LabelEntitiesModel = LabelEntitiesModel;
